import { Inject } from '@odinjs/odin';

import { CONFIRMATION_FIELD_VALID_EMAIL, CONFIRMATION_FIELD_VALID_PW, confirmationFieldValidatorEmail, confirmationFieldValidatorPassword, Endpoint } from '@philips-emr/hpms-provider-accreditation-commons';
import { HPMSOnClickTermUse, HPMSOnClickWarning, HpmsTermCheckboxAPI } from '@philips-emr/hpms-provider-accreditation-components';
import { FieldAPI, FormAPI, PanelAPI, ToastNotificationAPI, WizardAPI } from '@philips-emr/tws-components';
import {  ThemeTonalRange, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterNavigate, OnClick, OnMount, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { MountEvent, NavigateEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { NewUserAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

interface TermOfUse {
  nrSequence: number;
}

@Controller({ fragment: FragmentNames.NEW_USER })
export default class NewUserController extends BaseController {

  @Inject(Aliases.FORM_WIZARD_STEP_ACCOUNT_DATA)
  private formWizardStepAccountData: FormAPI;

  @Inject(Aliases.FORM_WIZARD_STEP_PERSONAL_INFORMATION)
  private formWizardStepPersonalInformation: FormAPI;

  @Inject(Aliases.FIELD_CONFIRM_EMAIL)
  private fieldConfirmEmail: FieldAPI;

  @Inject(Aliases.FIELD_CONFIRM_PW)
  private fieldConfirmPw: FieldAPI;

  @Inject(Aliases.HPMS_TERM_CHECKBOX)
  private checkboxTerms: HpmsTermCheckboxAPI;

  @Inject(Aliases.WIZARD_NEW_USER)
  private wizardNewUser: WizardAPI;

  @Inject(Aliases.PANEL_REGISTER_NEW_USER_SUCCESS)
  private panelRegisterNewUserSuccess: PanelAPI;

  @Inject(Aliases.PANEL_REGISTER_NEW_USER)
  private panelRegisterNewUser: PanelAPI;

  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;

  @Inject(Aliases.FIELD_TOAST)
  private fieldToast: FieldAPI;

  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;

  termOfUse: number;
  privacyWarning: number;
  private mapStepForm: Map<string, FormAPI> = new Map();

  @OnValidate(Aliases.FORM_WIZARD_STEP_PERSONAL_INFORMATION)
  onValidatePersonalInformationForm(event: ValidateEvent): void {
    const { valid } = event;
    this.wizardNewUser.setStepDone('wizard-step-personal-information', valid);
  }

  @OnValidate(Aliases.FORM_WIZARD_STEP_ACCOUNT_DATA)
  onValidateAccountDataForm(event: ValidateEvent): void {
    const { valid } = event;
    if (this.wizardNewUser.getCurrentStepIdentifier() == 'wizard-step-account-data') {
      this.wizardNewUser.setStepDone('wizard-step-account-data', valid);
    }
  }

  @OnMount([Aliases.FORM_WIZARD_STEP_PERSONAL_INFORMATION, Aliases.FORM_WIZARD_STEP_ACCOUNT_DATA])
  public onMountMapStepForm(event: MountEvent): void {
    const api = event.target as unknown as FormAPI;
    const alias = api.state.alias as string ?? '';
    const formName = alias.replace('form-', '');

    if (isEmptyOrBlankString(formName)) {
      return;
    }

    if (alias === Aliases.FORM_WIZARD_STEP_ACCOUNT_DATA) {
      void this.setTermOfUse();
      this.validateEmail();
      this.validatePassword();
    }

    this.mapStepForm.set(formName, api);
  }

  private validateEmail(): void {
    this.fieldConfirmEmail.addLocalValidator(CONFIRMATION_FIELD_VALID_EMAIL, (value) => {
      const confirmationValue = this.formWizardStepAccountData.getRecord()['dsEmail'] ?? '';
      return confirmationFieldValidatorEmail(value, confirmationValue);
    });
  }
  private validatePassword(): void {
    this.fieldConfirmPw.addLocalValidator(CONFIRMATION_FIELD_VALID_PW, (value) => {
      const confirmationValue = this.formWizardStepAccountData.getRecord()['dsPassword'] ?? '';
      return confirmationFieldValidatorPassword(value, confirmationValue);
    });
  }

  @OnAfterNavigate(Aliases.WIZARD_NEW_USER)
  public onAfterNavigate(event: NavigateEvent<string>): void {
    const { to } = event;

    if (!to) {
      return;
    }

    const formTo = this.mapStepForm.get(to);
    formTo?.setEditing(true);
  }

  @OnSubmit(Aliases.WIZARD_NEW_USER)
  onSubmitForm(): void {
    const body = { ...this.formWizardStepPersonalInformation.getRecord(),
      ...this.formWizardStepAccountData.getRecord(),
      nrSequenceTermsOfuse: this.termOfUse,
      nrSequencePrivacyWarning: this.privacyWarning };
    this.panelLoader.setVisible(true);
    this.wizardNewUser.setReadOnly(true);
    void HttpService.post(Endpoint.POST.NEW_USER, {}, { body })
      .then(() => {
        this.panelLoader.setVisible(false);
        this.wizardNewUser.setReadOnly(false);
        this.panelRegisterNewUser.setVisible(false);
        this.panelRegisterNewUserSuccess.setVisible(true);
      })
      .catch(({ body }) => {
        const response = body;
        this.panelRegisterNewUser.setVisible(true);
        this.panelLoader.setVisible(false);
        this.wizardNewUser.setReadOnly(false);
        this.fieldToast.setVisible(true);
        this.toast.setLevel(ToastLevel.ERROR);
        this.toast.setMessage(MessageService.translate(response.messages));
      });
  }

  async setTermOfUse(): Promise<void> {
    this.checkboxTerms.setEnabled(false);
    const responseTerm = await HttpService.get(Endpoint.GET.TERMS_OF_USE);
    const responsePrivacyWarning = await HttpService.get(Endpoint.GET.PRIVACY_WARNING);
    this.termOfUse = (responseTerm.body as TermOfUse).nrSequence;
    this.privacyWarning = (responsePrivacyWarning.body as TermOfUse).nrSequence;
    this.checkboxTerms.setEnabled(!!this.termOfUse && !!this.privacyWarning);
    this.context.set({ nrSeqTerm: this.termOfUse, nrSeqWarning: this.privacyWarning });
  }

  @HPMSOnClickWarning(Aliases.HPMS_TERM_CHECKBOX)
  onClickWarningCheckbox(): void {
    ModalService.openDialog({
      closeable: true,
      fragment: FragmentNames.PRIVACY_WARNING,
      title: 'pa.privacy-notice',
      theme: ThemeTonalRange.ULTRA_LIGHT,
    });
  }

  @HPMSOnClickTermUse(Aliases.HPMS_TERM_CHECKBOX)
  onClickTermCheckbox(): void {
    ModalService.openDialog({
      closeable: true,
      fragment: FragmentNames.TERM_OF_USE,
      title: 'pa.messages.terms-of-service',
      theme: ThemeTonalRange.ULTRA_LIGHT,
    });
  }

  @OnClick(Aliases.BUTTON_CONFIRMATION)
  onClickCloseModal(): void {
    ModalService.close('screen-new-user');
  }
}
